import React, { useRef } from "react";
import OSCard, { OS_TH_CLS, OS_PLAIN_DISABLED_CLS } from "./OSCard";

// Generic codebook-entry control table — one row per Subprocess.{inProcess,montageParams}
// entry (codebook-owned). Rows are defined entirely by the codebook: they cannot be
// added, removed, or reordered here. Only the "Параметри" value per entry is editable,
// keyed by the entry's stable _id and persisted by the caller.
// Excel export for this data now lives only in the master ExcelExportDropdown (see
// OS.tsx handleExportInProcess / handleExportMontageParams) — no per-card button here.

interface Props {
  title: string; // card heading, e.g. "InProcess"
  entriesLabel: string; // first data column header
  entries?: Array<{ _id: string; text: string }>;
  subprocessName?: string;
  values: Record<string, string>; // keyed by entry _id
  onChange: (entryId: string, value: string) => void;
}

const TH: React.FC<{ w: number; children: React.ReactNode }> = ({ w, children }) => (
  <th
    className={OS_TH_CLS}
    style={{ minWidth: w }}
  >
    {children}
  </th>
);

// Cell input (blue focus, matches OSProductivityTable), without text-right — free text.
const cellCls =
  // Canonical blue focus accent (CLAUDE.md hard rule — the yellow variant this carried is the
  // known accent bug, not a style choice).
  `w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${OS_PLAIN_DISABLED_CLS}`;

const OSSubprocessEntriesTable: React.FC<Props> = ({
  title,
  entriesLabel,
  entries,
  subprocessName,
  values,
  onChange,
}) => {
  // Refs for keyboard navigation between the (fixed, codebook-defined) rows.
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Rows are fixed, so navigation is pure focus movement — no add/remove.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === "ArrowDown" || e.key === "Enter") {
      e.preventDefault();
      inputRefs.current[i + 1]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      inputRefs.current[i - 1]?.focus();
    }
  };

  // The table is defined entirely by the codebook, so a subprocess with no
  // entries simply has no table.
  if (!entries || entries.length === 0) return null;

  // Widened card so the entry text + Параметри have more room (still capped so it
  // doesn't stretch full-bleed like the wide tables above it).
  return (
    <OSCard
      title={subprocessName ? `${title} — ${subprocessName}` : title}
      width="narrow"
    >
      <div className="overflow-x-auto">
        <table className="border-collapse w-full">
          <thead>
            <tr>
              <TH w={50}>Р.бр.</TH>
              <TH w={440}>{entriesLabel}</TH>
              <TH w={240}>Параметри</TH>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => (
              <tr key={entry._id} className="hover:bg-blue-50 focus-within:bg-blue-50">
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-center text-[11px] text-gray-700">
                  {i + 1}
                </td>
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5 text-[11px] text-gray-700">
                  {entry.text}
                </td>
                <td className="border border-gray-300 h-[26px] px-1.5 py-0.5">
                  <input
                    ref={(el) => { inputRefs.current[i] = el; }}
                    className={cellCls}
                    value={values[entry._id] ?? ""}
                    onChange={(e) => onChange(entry._id, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </OSCard>
  );
};

export default OSSubprocessEntriesTable;
